// src/utils/expirationPredictor.js

import { AGRICULTURAL_CATEGORIES, getCategoryConfig } from './agriculturalCategories';

/**
 * Storage conditions for different product types
 */
export const STORAGE_CONDITIONS = {
  AMBIENT: 'ambient',
  REFRIGERATED: 'refrigerated',
  FROZEN: 'frozen',
  CONTROLLED_ATMOSPHERE: 'controlled_atmosphere',
  VACUUM_SEALED: 'vacuum_sealed',
  MODIFIED_ATMOSPHERE: 'modified_atmosphere',
  DRY_STORAGE: 'dry_storage'
};

/**
 * Product states that affect expiration
 */
export const PRODUCT_STATES = {
  FRESH: 'fresh',
  DRIED: 'dried',
  CANNED: 'canned',
  FROZEN: 'frozen',
  PROCESSED: 'processed',
  POWDERED: 'powdered',
  OIL: 'oil',
  EXTRACT: 'extract'
};

/**
 * Default shelf life ranges in days for each category under different conditions
 * Structure: [minDays, typicalDays, maxDays]
 */
const DEFAULT_SHELF_LIFE_RANGES = {
  cereals: {
    [PRODUCT_STATES.FRESH]: [180, 365, 730],
    [PRODUCT_STATES.DRIED]: [365, 730, 1095],
    [PRODUCT_STATES.PROCESSED]: [90, 180, 365]
  },
  legumes: {
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.FRESH]: [7, 14, 30],
    [PRODUCT_STATES.CANNED]: [365, 730, 1095]
  },
  fruits: {
    [PRODUCT_STATES.FRESH]: { 
      ambient: [2, 7, 30],
      refrigerated: [7, 21, 90],
      frozen: [180, 365, 730]
    },
    [PRODUCT_STATES.DRIED]: [180, 365, 730],
    [PRODUCT_STATES.CANNED]: [365, 730, 1825]
  },
  vegetables: {
    [PRODUCT_STATES.FRESH]: {
      ambient: [3, 10, 30],
      refrigerated: [7, 21, 60],
      frozen: [180, 365, 730]
    },
    [PRODUCT_STATES.CANNED]: [365, 730, 1825]
  },
  tubers: {
    [PRODUCT_STATES.FRESH]: {
      ambient: [14, 60, 180],
      refrigerated: [30, 90, 180],
      dry_storage: [60, 120, 365]
    }
  },
  oilseeds: {
    [PRODUCT_STATES.FRESH]: [90, 180, 365],
    [PRODUCT_STATES.DRIED]: [180, 365, 730],
    [PRODUCT_STATES.OIL]: [365, 730, 1095]
  },
  nuts: {
    [PRODUCT_STATES.FRESH]: [30, 90, 180],
    [PRODUCT_STATES.DRIED]: [180, 365, 730],
    [PRODUCT_STATES.ROASTED]: [90, 180, 365]
  },
  spices: {
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.POWDERED]: [180, 365, 730],
    [PRODUCT_STATES.FRESH]: [7, 14, 30]
  },
  herbs: {
    [PRODUCT_STATES.FRESH]: {
      ambient: [2, 5, 10],
      refrigerated: [5, 10, 21],
      dried: [180, 365, 730]
    }
  },
  beverages: {
    [PRODUCT_STATES.FRESH]: [30, 90, 180],
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.PROCESSED]: [180, 365, 730]
  },
  pulses: {
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.CANNED]: [365, 730, 1095]
  },
  grains: {
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.FLOUR]: [180, 365, 540]
  },
  mushrooms: {
    [PRODUCT_STATES.FRESH]: {
      ambient: [1, 3, 7],
      refrigerated: [5, 10, 21],
      dried: [365, 730, 1095]
    }
  },
  medicinal: {
    [PRODUCT_STATES.DRIED]: [365, 730, 1825],
    [PRODUCT_STATES.EXTRACT]: [180, 365, 730],
    [PRODUCT_STATES.POWDERED]: [180, 365, 540]
  }
};

/**
 * Critical factors that affect expiration
 */
export const EXPIRATION_FACTORS = {
  TEMPERATURE: 'temperature',
  HUMIDITY: 'humidity',
  LIGHT_EXPOSURE: 'light_exposure',
  OXYGEN_EXPOSURE: 'oxygen_exposure',
  PACKAGING: 'packaging',
  PROCESSING_METHOD: 'processing_method',
  INITIAL_QUALITY: 'initial_quality',
  CONTAMINATION_RISK: 'contamination_risk'
};

/**
 * Calculates expiration date based on multiple factors
 * @param {Object} params - Input parameters
 * @param {string} params.category - Product category ID
 * @param {Date} params.productionDate - Production/harvest date
 * @param {string} params.productState - Product state (fresh, dried, etc.)
 * @param {string} params.storageCondition - Storage condition
 * @param {Object} params.factors - Additional factors affecting shelf life
 * @returns {Object} - Expiration prediction results
 */
export const calculateExpiration = ({
  category,
  productionDate,
  productState = PRODUCT_STATES.FRESH,
  storageCondition = STORAGE_CONDITIONS.AMBIENT,
  factors = {}
}) => {
  const categoryConfig = getCategoryConfig(category);
  const baseShelfLife = getBaseShelfLife(categoryConfig.id, productState, storageCondition);
  
  if (!baseShelfLife) {
    return {
      success: false,
      error: 'Cannot determine shelf life for this product configuration'
    };
  }

  // Apply factor adjustments
  const adjustedShelfLife = applyFactorAdjustments(baseShelfLife, factors);
  
  // Calculate expiration date
  const production = new Date(productionDate);
  const expirationDate = new Date(production);
  expirationDate.setDate(production.getDate() + adjustedShelfLife.typical);
  
  // Calculate warning dates
  const warningDate = new Date(expirationDate);
  warningDate.setDate(warningDate.getDate() - Math.floor(adjustedShelfLife.typical * 0.2));
  
  const criticalDate = new Date(expirationDate);
  criticalDate.setDate(criticalDate.getDate() - Math.floor(adjustedShelfLife.typical * 0.1));
  
  return {
    success: true,
    category: categoryConfig.name,
    productionDate: production.toISOString().split('T')[0],
    expirationDate: expirationDate.toISOString().split('T')[0],
    warningDate: warningDate.toISOString().split('T')[0],
    criticalDate: criticalDate.toISOString().split('T')[0],
    shelfLife: {
      minDays: adjustedShelfLife.min,
      typicalDays: adjustedShelfLife.typical,
      maxDays: adjustedShelfLife.max,
      confidence: calculateConfidence(categoryConfig.id, productState, factors)
    },
    recommendations: getStorageRecommendations(categoryConfig.id, productState, storageCondition),
    riskLevel: calculateRiskLevel(expirationDate, adjustedShelfLife.typical)
  };
};

/**
 * Get base shelf life for a category
 */
const getBaseShelfLife = (categoryId, productState, storageCondition) => {
  const categoryRanges = DEFAULT_SHELF_LIFE_RANGES[categoryId];
  
  if (!categoryRanges) {
    // Try to find similar category
    const similarCategories = {
      pulses: 'legumes',
      grains: 'cereals'
    };
    const similarCategory = similarCategories[categoryId];
    if (similarCategory && DEFAULT_SHELF_LIFE_RANGES[similarCategory]) {
      return extractShelfLife(DEFAULT_SHELF_LIFE_RANGES[similarCategory], productState, storageCondition);
    }
    return null;
  }
  
  return extractShelfLife(categoryRanges, productState, storageCondition);
};

/**
 * Extract shelf life from nested structure
 */
const extractShelfLife = (categoryRanges, productState, storageCondition) => {
  const stateRanges = categoryRanges[productState];
  
  if (!stateRanges) {
    // Default to first available state
    const firstState = Object.keys(categoryRanges)[0];
    return categoryRanges[firstState];
  }
  
  // Handle nested storage condition structure
  if (typeof stateRanges === 'object' && !Array.isArray(stateRanges)) {
    const conditionRanges = stateRanges[storageCondition];
    return conditionRanges ? { min: conditionRanges[0], typical: conditionRanges[1], max: conditionRanges[2] } : null;
  }
  
  // Simple array structure
  return Array.isArray(stateRanges) 
    ? { min: stateRanges[0], typical: stateRanges[1], max: stateRanges[2] }
    : null;
};

/**
 * Apply adjustments based on various factors
 */
const applyFactorAdjustments = (baseShelfLife, factors) => {
  let adjustments = {
    min: baseShelfLife.min,
    typical: baseShelfLife.typical,
    max: baseShelfLife.max
  };
  
  // Temperature adjustment
  if (factors.temperature) {
    const tempEffect = Math.max(0.5, Math.min(1.5, 1 + (25 - factors.temperature) * 0.02));
    adjustments.typical *= tempEffect;
    adjustments.min *= tempEffect;
    adjustments.max *= tempEffect;
  }
  
  // Humidity adjustment
  if (factors.humidity) {
    const humidityEffect = factors.humidity > 70 ? 0.8 : factors.humidity < 30 ? 1.2 : 1;
    adjustments.typical *= humidityEffect;
  }
  
  // Packaging adjustment
  if (factors.packaging) {
    const packagingMultipliers = {
      'vacuum_sealed': 1.5,
      'modified_atmosphere': 1.3,
      'airtight': 1.2,
      'plastic_bag': 1,
      'open_container': 0.7
    };
    adjustments.typical *= packagingMultipliers[factors.packaging] || 1;
  }
  
  // Initial quality adjustment
  if (factors.initialQuality) {
    const qualityMultipliers = {
      'excellent': 1.2,
      'good': 1,
      'fair': 0.8,
      'poor': 0.6
    };
    adjustments.typical *= qualityMultipliers[factors.initialQuality] || 1;
  }
  
  // Round to nearest day
  adjustments.min = Math.round(adjustments.min);
  adjustments.typical = Math.round(adjustments.typical);
  adjustments.max = Math.round(adjustments.max);
  
  return adjustments;
};

/**
 * Calculate confidence level for prediction
 */
const calculateConfidence = (categoryId, productState, factors) => {
  let confidence = 0.7; // Base confidence
  
  // Increase confidence for stable products
  const stableCategories = ['cereals', 'legumes', 'pulses', 'grains', 'spices'];
  if (stableCategories.includes(categoryId)) confidence += 0.2;
  
  // Decrease confidence for perishable products
  const perishableCategories = ['fruits', 'vegetables', 'herbs', 'mushrooms'];
  if (perishableCategories.includes(categoryId)) confidence -= 0.1;
  
  // Increase if we have processing state
  if (productState && productState !== PRODUCT_STATES.FRESH) confidence += 0.1;
  
  // Adjust based on available factors
  const factorCount = Object.keys(factors).length;
  confidence += factorCount * 0.05;
  
  return Math.min(0.95, Math.max(0.5, confidence));
};

/**
 * Get storage recommendations for a product
 */
const getStorageRecommendations = (categoryId, productState, storageCondition) => {
  const recommendations = [];
  
  // General recommendations
  recommendations.push(`Store at appropriate temperature for ${productState} ${getCategoryName(categoryId)}`);
  
  // Category-specific recommendations
  switch (categoryId) {
    case 'fruits':
    case 'vegetables':
      if (productState === PRODUCT_STATES.FRESH) {
        recommendations.push('Keep away from ethylene-producing fruits');
        recommendations.push('Store in well-ventilated containers');
      }
      break;
    case 'cereals':
    case 'grains':
      recommendations.push('Store in airtight containers to prevent pest infestation');
      recommendations.push('Keep in cool, dry place away from sunlight');
      break;
    case 'spices':
      recommendations.push('Store in dark, airtight containers');
      recommendations.push('Keep away from heat sources');
      break;
    case 'nuts':
      recommendations.push('Refrigerate or freeze for long-term storage');
      recommendations.push('Store in airtight containers to prevent rancidity');
      break;
    case 'herbs':
      if (productState === PRODUCT_STATES.FRESH) {
        recommendations.push('Store with stems in water or wrap in damp paper towels');
      }
      break;
  }
  
  // State-specific recommendations
  if (productState === PRODUCT_STATES.DRIED) {
    recommendations.push('Maintain humidity below 60%');
  }
  
  if (productState === PRODUCT_STATES.FROZEN) {
    recommendations.push('Use freezer-safe packaging to prevent freezer burn');
  }
  
  return recommendations;
};

/**
 * Calculate risk level based on remaining shelf life
 */
const calculateRiskLevel = (expirationDate, typicalShelfLife) => {
  const now = new Date();
  const expiration = new Date(expirationDate);
  const daysRemaining = Math.floor((expiration - now) / (1000 * 60 * 60 * 24));
  const percentageRemaining = (daysRemaining / typicalShelfLife) * 100;
  
  if (daysRemaining < 0) return 'expired';
  if (percentageRemaining < 10) return 'critical';
  if (percentageRemaining < 30) return 'high';
  if (percentageRemaining < 60) return 'medium';
  return 'low';
};

/**
 * Get category name from ID
 */
const getCategoryName = (categoryId) => {
  const category = AGRICULTURAL_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.name : 'Unknown Category';
};

/**
 * Get all available product states for a category
 */
export const getAvailableStatesForCategory = (categoryId) => {
  const stateMap = {
    cereals: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.PROCESSED, PRODUCT_STATES.FLOUR],
    legumes: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.CANNED],
    fruits: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.CANNED, PRODUCT_STATES.FROZEN],
    vegetables: [PRODUCT_STATES.FRESH, PRODUCT_STATES.CANNED, PRODUCT_STATES.FROZEN],
    tubers: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.PROCESSED],
    oilseeds: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.OIL],
    nuts: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.ROASTED],
    spices: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.POWDERED],
    herbs: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED],
    beverages: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED, PRODUCT_STATES.PROCESSED],
    pulses: [PRODUCT_STATES.DRIED, PRODUCT_STATES.CANNED],
    grains: [PRODUCT_STATES.DRIED, PRODUCT_STATES.FLOUR],
    mushrooms: [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED],
    medicinal: [PRODUCT_STATES.DRIED, PRODUCT_STATES.EXTRACT, PRODUCT_STATES.POWDERED]
  };
  
  return stateMap[categoryId] || [PRODUCT_STATES.FRESH, PRODUCT_STATES.DRIED];
};

/**
 * Get optimal storage condition for a category and state
 */
export const getOptimalStorageCondition = (categoryId, productState) => {
  const optimalMap = {
    fruits: {
      [PRODUCT_STATES.FRESH]: STORAGE_CONDITIONS.REFRIGERATED,
      [PRODUCT_STATES.DRIED]: STORAGE_CONDITIONS.DRY_STORAGE,
      [PRODUCT_STATES.FROZEN]: STORAGE_CONDITIONS.FROZEN
    },
    vegetables: {
      [PRODUCT_STATES.FRESH]: STORAGE_CONDITIONS.REFRIGERATED,
      [PRODUCT_STATES.FROZEN]: STORAGE_CONDITIONS.FROZEN
    },
    herbs: {
      [PRODUCT_STATES.FRESH]: STORAGE_CONDITIONS.REFRIGERATED,
      [PRODUCT_STATES.DRIED]: STORAGE_CONDITIONS.DRY_STORAGE
    },
    mushrooms: {
      [PRODUCT_STATES.FRESH]: STORAGE_CONDITIONS.REFRIGERATED,
      [PRODUCT_STATES.DRIED]: STORAGE_CONDITIONS.DRY_STORAGE
    },
    tubers: {
      [PRODUCT_STATES.FRESH]: STORAGE_CONDITIONS.DRY_STORAGE
    }
  };
  
  return optimalMap[categoryId]?.[productState] || STORAGE_CONDITIONS.AMBIENT;
};

/**
 * Quick estimate function for simple use cases
 */
export const quickExpirationEstimate = (categoryName, productionDate) => {
  const categoryConfig = getCategoryConfig(categoryName);
  
  return calculateExpiration({
    category: categoryConfig.id,
    productionDate,
    productState: PRODUCT_STATES.FRESH,
    storageCondition: getOptimalStorageCondition(categoryConfig.id, PRODUCT_STATES.FRESH)
  });
};

/**
 * Batch expiration calculation for inventory
 */
export const calculateBatchExpirations = (items) => {
  return items.map(item => ({
    ...item,
    expiration: calculateExpiration({
      category: item.category,
      productionDate: item.productionDate || item.receivedDate,
      productState: item.state || PRODUCT_STATES.FRESH,
      storageCondition: item.storageCondition || STORAGE_CONDITIONS.AMBIENT,
      factors: {
        temperature: item.storageTemperature,
        humidity: item.storageHumidity,
        packaging: item.packagingType,
        initialQuality: item.qualityGrade
      }
    })
  }));
};

/**
 * Monitor expiring products and send alerts
 */
export const monitorExpiringProducts = (products, daysThreshold = 7) => {
  const now = new Date();
  const alerts = [];
  
  products.forEach(product => {
    const expirationResult = product.expiration || 
      calculateExpiration({
        category: product.category,
        productionDate: product.productionDate,
        productState: product.state,
        storageCondition: product.storageCondition
      });
    
    if (expirationResult.success) {
      const expirationDate = new Date(expirationResult.expirationDate);
      const daysRemaining = Math.floor((expirationDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= daysThreshold && daysRemaining >= 0) {
        alerts.push({
          productId: product.id,
          productName: product.name,
          daysRemaining,
          expirationDate: expirationResult.expirationDate,
          riskLevel: expirationResult.riskLevel,
          recommendations: expirationResult.recommendations
        });
      }
    }
  });
  
  return alerts.sort((a, b) => a.daysRemaining - b.daysRemaining);
};

export default {
  calculateExpiration,
  quickExpirationEstimate,
  calculateBatchExpirations,
  monitorExpiringProducts,
  getAvailableStatesForCategory,
  getOptimalStorageCondition,
  STORAGE_CONDITIONS,
  PRODUCT_STATES,
  EXPIRATION_FACTORS
};